import React, { CSSProperties } from 'react';

/**
 * Bullet count = ceil(totalBounty / bountyAmount)
 *
 * Mirrors Unity's UIPlayer.cs:1472 — one bullet per bounty knockout, where
 * each knockout pays out `bountyAmount` chips. The ceil() means a partial
 * bounty still earns a bullet (defensive — partial bounties shouldn't
 * happen in normal play but the engine has been known to fractionally
 * award bounties when multiple players bust on the same hand). Falls
 * back to 0 on bad input — bountyAmount=0 (non-bounty tournament cached
 * in the slot before gameType updates) or negative values.
 *
 * Exported for unit testing.
 */
export const computeBulletCount = (totalBounty: number, bountyAmount: number): number => {
  if (!Number.isFinite(totalBounty) || totalBounty <= 0) return 0;
  if (!Number.isFinite(bountyAmount) || bountyAmount <= 0) return 0;
  return Math.ceil(totalBounty / bountyAmount);
};

type BountyBulletsPropType = {
  totalBounty: number;
  bountyAmount: number;
  gameType?: string;
  /** Inline style override for the outer container. Used by SeatContainer to
   *  absolutely-position the badge on the top edge of each pill variant. */
  style?: CSSProperties;
};

/**
 * Compact bullet badge that sits on the top edge of the player's seat
 * pill in Bounty tournaments. Renders a single bullet icon followed by
 * `×N` (where N = totalBounty / bountyAmount). Scales gracefully to any
 * knockout count without overflowing the pill — easier to count at a
 * glance than a literal row of icons.
 *
 * Renders nothing unless:
 *   - gameType === 'Bounty' (other formats don't show knockouts), AND
 *   - computeBulletCount > 0 (no badge while the seat has no kills yet).
 *
 * Positioning is controlled by the parent via the `style` prop — the
 * SeatContainer drops one into each of the four pill variants (portrait
 * self / opponent, landscape self / opponent) with the appropriate
 * absolute placement for that layout.
 */
export const BountyBullets = ({
  totalBounty,
  bountyAmount,
  gameType,
  style
}: BountyBulletsPropType) => {
  if (gameType !== 'Bounty') return null;

  const bulletCount = computeBulletCount(totalBounty, bountyAmount);
  if (bulletCount === 0) return null;

  return (
    <div
      style={{ ...containerStyle, ...style }}
      data-testid='bounty-bullets'
      aria-label={`${bulletCount} knockouts`}>
      <BulletIcon />
      <span style={countStyle}>×{bulletCount}</span>
    </div>
  );
};

// Brighter, more saturated yellow-gold palette so the badge reads as a
// vivid achievement chip rather than aged brass. Reference shot showed
// pure yellow bodies with a warm gold tip and a thin warm-brown stroke.
const BULLET_TIP_HIGHLIGHT = '#fff8c4';
const BULLET_TIP = '#ffc83d';
const BULLET_BODY_TOP = '#ffe14a';
const BULLET_BODY_BOTTOM = '#e6a92a';
const BULLET_RIM = '#c98815';
const BULLET_STROKE = 'rgba(90, 55, 5, 0.7)';

/**
 * Stylized brass cartridge — pointed tip + casing body + rim.
 * Linear gradient on the body and a highlight strip on the tip give it
 * the polished-metal feel; the rim is darker so the base reads as a
 * separate part rather than blurring into the body.
 */
const BulletIcon = () => (
  <svg width='7' height='16' viewBox='0 0 8 18' style={bulletSvgStyle} aria-hidden='true'>
    <defs>
      <linearGradient id='bb-body' x1='0' y1='0' x2='1' y2='0'>
        <stop offset='0%' stopColor={BULLET_BODY_BOTTOM} />
        <stop offset='40%' stopColor={BULLET_BODY_TOP} />
        <stop offset='100%' stopColor={BULLET_BODY_BOTTOM} />
      </linearGradient>
      <linearGradient id='bb-tip' x1='0' y1='0' x2='1' y2='0'>
        <stop offset='0%' stopColor={BULLET_TIP} />
        <stop offset='40%' stopColor={BULLET_TIP_HIGHLIGHT} />
        <stop offset='100%' stopColor={BULLET_TIP} />
      </linearGradient>
    </defs>
    {/* tip — bullet nose with curved sides */}
    <path
      d='M4 0.5 C5.5 0.5 7 2 7 4.5 L1 4.5 C1 2 2.5 0.5 4 0.5 Z'
      fill='url(#bb-tip)'
      stroke={BULLET_STROKE}
      strokeWidth='0.5'
    />
    {/* body — main casing */}
    <rect
      x='1'
      y='4.5'
      width='6'
      height='10'
      fill='url(#bb-body)'
      stroke={BULLET_STROKE}
      strokeWidth='0.5'
    />
    {/* rim — darker base for depth */}
    <rect
      x='0.5'
      y='14.5'
      width='7'
      height='3'
      rx='0.5'
      fill={BULLET_RIM}
      stroke={BULLET_STROKE}
      strokeWidth='0.5'
    />
    {/* tiny highlight on left of body for the polished-brass cue */}
    <rect x='1.5' y='5' width='0.5' height='9' fill='rgba(255,255,255,0.35)' />
  </svg>
);

/** Dark backdrop pill — keeps the bullets readable against any seat
 *  background (active turn glow, winner border, action-color tint). */
const containerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'flex-end',
  gap: '1px',
  padding: '2px 5px 1px',
  borderRadius: '10px',
  background: 'rgba(15, 10, 5, 0.85)',
  border: '1px solid rgba(184, 134, 11, 0.55)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.5), 0 0 6px rgba(255, 200, 60, 0.3)',
  pointerEvents: 'none',
  whiteSpace: 'nowrap'
};

const bulletSvgStyle: CSSProperties = {
  display: 'block',
  flexShrink: 0
};

const countStyle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 800,
  color: BULLET_BODY_TOP,
  letterSpacing: '0.02em',
  marginLeft: '3px',
  marginBottom: '1px',
  textShadow: '0 1px 1px rgba(0,0,0,0.6)',
  // Tabular figures so multi-digit counts don't shift width per render.
  fontVariantNumeric: 'tabular-nums'
};

export default BountyBullets;
